/**
 * THE FLAG STAND AND THE TIMING TOWER.
 *
 * Race identity, the clock, the flag, and the three things a player does at the
 * table: run one turn, let it run, stop it.
 *
 * The whole race is resolved by the engine the moment it is set up, and this
 * walks a cursor through the turns the engine produced. Advancing does not
 * re-run anything and cannot change an outcome — it is the tape, not the game.
 */

import { useState } from 'react';
import type { TurnResult } from '../domain/turn.ts';
import type { TrackCard } from '../domain/turn.ts';
import type { Flag } from './boardModel.ts';
import { FLAG_LABEL, lapsRemaining, turnsRemaining } from './boardModel.ts';
import { raceAccentProps, useRaceAccent } from './raceAccent.ts';
import { useCiteText } from '../lib/prefs.ts';
import { PACE_OPTIONS, type PaceKey } from '../presentation/bigMoments.ts';

/**
 * D-EZ-103 — the pace dial: Big moments (stops to roll the turns worth rolling,
 * see `presentation/bigMoments.ts`), Slow, Race pace, Quick.
 */
export const PACES = PACE_OPTIONS;

export interface TurnControlsProps {
  raceName: string;
  track: TrackCard;
  /** D-026 — the named race's key, when known, so its crest's accent can tint the header. */
  raceKey?: string | null;
  flag: Flag;
  /** The turn about to be run — `RaceState.turn` after the last one played. */
  nextTurn: number;
  scheduledTurns: number;
  turnsRun: number;
  /** How many turns of the tape have been played. */
  cursor: number;
  /** The turn just played. */
  turn: TurnResult | null;
  /** Turns on which the field pits (p.xi), straight off the engine's schedule. */
  pitTurns: readonly number[];
  playing: boolean;
  pace: PaceKey;
  onPaceChange: (pace: PaceKey) => void;
  onPlay: () => void;
  onPause: () => void;
  onToTheFlag: () => void;
  onRewind: () => void;
  /** UI-001 — one turn back, so a move can be re-watched without restarting. */
  onStepBack: () => void;
  /**
   * Throw this race away and set up another. Exhibition only, and only while the
   * race is still running — a season race is part of the championship, and a
   * finished exhibition offers its own way on (`ExhibitionHandoff`).
   */
  onNewRace?: () => void;
}

export default function TurnControls(props: TurnControlsProps) {
  const ct = useCiteText();
  /** F4 — whether the New race panel is showing. Never destructive by itself. */
  const [arming, setArming] = useState(false);
  const {
    raceName, track, flag, nextTurn, scheduledTurns, turnsRun, cursor, turn, pitTurns,
    playing, pace, onPaceChange, onPlay, onPause, onToTheFlag, onRewind, onStepBack, onNewRace,
  } = props;

  const atEnd = cursor >= turnsRun;
  const left = turnsRemaining(nextTurn, scheduledTurns);
  const laps = lapsRemaining(left, track.lengthMiles);
  const extra = turnsRun > scheduledTurns ? turnsRun - scheduledTurns : 0;
  const nextIsPit = !atEnd && pitTurns.includes(nextTurn);
  const accent = useRaceAccent(props.raceKey, track.id);

  return (
    <header className="tower" {...raceAccentProps(accent)}>
      <div className="tower__id">
        <h1 className="tower__race">{raceName}</h1>
        <p className="tower__track">
          <span>{track.name}</span>
          <span className="sep" aria-hidden="true">/</span>
          <span className="dim">{track.category}</span>
          {track.location && (
            <>
              <span className="sep" aria-hidden="true">/</span>
              <span className="dim">{track.location}</span>
            </>
          )}
          {track.lengthMiles && (
            <>
              <span className="sep" aria-hidden="true">/</span>
              <span className="dim">{track.lengthMiles} mi</span>
            </>
          )}
        </p>
      </div>

      <div className={`flagstand flagstand--${flag}`} role="status" aria-live="polite">
        <span className="flagstand__cloth" aria-hidden="true" />
        <span className="flagstand__word">{FLAG_LABEL[flag]}</span>
        {turn?.kind === 'pit' && <span className="flagstand__sub">pit road open</span>}
        {flag === 'yellow' && <span className="flagstand__sub">pace car out</span>}
        {flag === 'white' && <span className="flagstand__sub">last scheduled turn</span>}
      </div>

      <dl className="clock">
        <div className="clock__cell">
          <dt>Turn</dt>
          <dd className="clock__big">
            {cursor === 0 ? '—' : cursor}
            <span className="clock__of">/ {scheduledTurns}</span>
          </dd>
        </div>
        <div className="clock__cell">
          <dt>Turns left</dt>
          <dd className="clock__big">{left}</dd>
        </div>
        <div className="clock__cell">
          <dt title={ct("Ten miles a turn (RB/ii) over the track's printed length. Derived for the booth — the engine's clock is turns.", "Ten miles a turn over the track's length.")}>
            Laps left
          </dt>
          <dd className="clock__big">{laps === null ? '—' : laps}</dd>
        </div>
        <div className="clock__cell">
          <dt>Next</dt>
          <dd className="clock__note">
            {atEnd
              ? extra > 0
                ? `finished · ${extra} extra turn${extra === 1 ? '' : 's'}`
                : 'finished'
              : nextIsPit
                ? `turn ${nextTurn} · pit stop`
                : `turn ${nextTurn}`}
          </dd>
        </div>
      </dl>

      {/*
        D-EZ-73 (F4) — THE PLAYBACK ROW, DIVIDED, AND THE DESTRUCTIVE ONE TAKEN OUT.

        From the design review, 20 Aug 2026:

          "New race throws away the race you're watching and it wears exactly
           the same quiet button as Run it back, which costs nothing. Two of its
           neighbours are undos. The muscle memory that lands on one will
           eventually land on the other."

        So: three groups with a rule between them — forward, back, pace — and
        five verbs are no longer one undivided line to scan. `New race` is out of
        the group entirely, because it is not playback, and it is ARMED rather
        than immediate. That pattern is `CareerScreen`'s `wipe` panel, which
        exists for the same reason and works.
      */}
      <div className="controls" role="group" aria-label="Race playback">
        {/*
          D-EZ-101 — ONE "Roll next turn". It lives on the dice table
          (RaceDiceWorkbench), where the dice it rolls are, and it changes its
          own wording to "Reveal next turn" when dice motion is off. This row
          kept a second copy that did the same thing with a different label.
          The keyboard path is unchanged: → still steps a turn.
        */}
        <div className="controls__group" role="group" aria-label="Forward">
          {playing ? (
            <button type="button" className="btn btn--hot" onClick={onPause}>
              Pause
            </button>
          ) : (
            <button type="button" className="btn btn--hot" onClick={onPlay} disabled={atEnd}>
              Run
            </button>
          )}
          <button type="button" className="btn" onClick={onToTheFlag} disabled={atEnd}>
            To the flag
          </button>
        </div>

        <div className="controls__group" role="group" aria-label="Back">
          <button
            type="button"
            className="btn btn--quiet"
            onClick={onStepBack}
            disabled={cursor === 0}
            title="Re-watch the last turn"
          >
            Run it back
          </button>
          <button type="button" className="btn btn--quiet" onClick={onRewind} disabled={cursor === 0}>
            Back to the green
          </button>
        </div>

        <div className="controls__group">
          <label className="pace">
            <span className="pace__label">Pace</span>
            <select
              className="pace__select"
              value={pace}
              onChange={(e) => onPaceChange(e.target.value as PaceKey)}
            >
              {PACES.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {onNewRace && !atEnd && (
        <div className="controls__end">
          <button
            type="button"
            className={`btn btn--danger-quiet${arming ? ' is-armed' : ''}`}
            aria-expanded={arming}
            onClick={() => setArming((was) => !was)}
          >
            New race
          </button>
        </div>
        )}
      </div>

      {/*
        ⚠ ARMED, NOT CONFIRMED — the same shape the season wipe uses. The first
        press does not throw anything away; it puts a line on screen that NAMES
        what is about to go, and the press that actually does it is a second,
        differently-worded button.
      */}
      {arming && onNewRace && (
        <div className="newrace" role="group" aria-label="Start a new race">
          <p className="newrace__what">
            This throws away the race you are watching — turn {cursor} of {turnsRun} — and sets up a fresh
            one. Nothing in the season or the career is touched.
          </p>
          <div className="newrace__row">
            <button
              type="button"
              className="btn btn--hot"
              onClick={() => {
                setArming(false);
                onNewRace();
              }}
            >
              Throw this race away
            </button>
            <button type="button" className="btn btn--quiet" onClick={() => setArming(false)}>
              Keep watching
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
